import { useEffect, useState } from "react";
import { FileText, Upload, Trash2, Power, Download, PenLine } from "lucide-react";
import { http, fmtDateTime, errMsg, downloadFile } from "../api";

export default function AdminAgreements() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [items, setItems] = useState([]);
  const [signed, setSigned] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", file: null });
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => {
    http.get("/admin/agreements", { params: { client_id: clientId } }).then(r => setItems(r.data));
    http.get("/admin/agreements/signed", { params: { client_id: clientId } }).then(r => setSigned(r.data));
  };
  useEffect(() => { http.get("/admin/clients").then(r => setClients(r.data)); }, []);
  useEffect(() => { load(); }, [clientId]);

  const upload = async e => {
    e.preventDefault();
    if (!clientId || !form.file) { setNotice({ tone: "error", text: "Choose a client and a PDF" }); return; }
    setBusy(true); setNotice(null);
    const fd = new FormData(); fd.append("client_id", clientId); fd.append("title", form.title); fd.append("description", form.description); fd.append("file", form.file);
    try { await http.post("/admin/agreements", fd, { headers: { "Content-Type": "multipart/form-data" } }); setNotice({ tone: "success", text: "Agreement uploaded. Every new hire from this client will require it." }); setForm({ title: "", description: "", file: null }); load(); }
    catch (e) { setNotice({ tone: "error", text: errMsg(e) }); } finally { setBusy(false); }
  };
  const toggle = async a => { await http.patch(`/admin/agreements/${a.id}`); load(); };
  const remove = async a => { if (!window.confirm(`Delete "${a.title}"? Already-signed copies stay saved.`)) return; await http.delete(`/admin/agreements/${a.id}`); load(); };
  const clientLabel = c => `${c.company || c.name}${c.company ? ` (${c.name})` : ""}`;

  return (
    <>
      <div className="page-head"><div><h1>Agreements</h1><p>Upload agreements per client. Artists must e-sign them before accepting that client's hire request.</p></div></div>
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-head"><h2>Upload agreement</h2></div>
        <form onSubmit={upload}>
          <div className="field-row">
            <div><label>On behalf of client *</label>
              <select value={clientId} onChange={e => setClientId(e.target.value)} data-testid="agreement-client"><option value="">Select client</option>{clients.map(c => <option key={c.id} value={c.id}>{clientLabel(c)}</option>)}</select>
            </div>
            <div><label>Agreement title *</label><input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. NDA, Voice usage rights" data-testid="agreement-title" /></div>
          </div>
          <div><label>Short description (optional)</label><input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="agreement-description" /></div>
          <label className="dropzone" style={{ padding: 16, marginTop: 8 }}>
            <input type="file" accept="application/pdf" onChange={e => setForm({ ...form, file: e.target.files[0] })} data-testid="agreement-file" />
            <div className="dropzone-icon"><Upload size={18} /></div>
            <p>{form.file ? form.file.name : "Click to select agreement PDF"}</p>
          </label>
          {notice && <div className={`notice ${notice.tone}`} style={{ marginTop: 10 }} data-testid="agreement-notice">{notice.text}</div>}
          <button className="primary" type="submit" disabled={busy} style={{ marginTop: 10 }} data-testid="agreement-upload"><Upload size={14} /> {busy ? "Uploading…" : "Upload agreement"}</button>
        </form>
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-head"><h2>Active agreements {clientId ? "for this client" : "(all clients)"}</h2><span className="badge">{items.length}</span></div>
        <div className="list-rows">
          {items.map(a => (
            <div key={a.id} className="list-row" data-testid={`agreement-row-${a.id}`}>
              <FileText size={18} color={a.active ? "var(--primary)" : "var(--muted)"} />
              <div className="list-row-info"><h3>{a.title}</h3><p>{a.client_company || a.client_name} · {a.pages} page(s) · {a.signed_count} signed · Added {fmtDateTime(a.created_at)}</p></div>
              <div className="list-row-actions">
                <span className={`badge ${a.active ? "success" : ""}`}>{a.active ? "Required" : "Paused"}</span>
                <button className="pill-action neutral" onClick={() => downloadFile(`/files/${a.id}`, {}, a.filename)} data-testid={`agreement-download-${a.id}`}><Download size={13} /></button>
                <button className="pill-action neutral" onClick={() => toggle(a)} data-testid={`agreement-toggle-${a.id}`}><Power size={13} /></button>
                <button className="pill-action reject" onClick={() => remove(a)} data-testid={`agreement-delete-${a.id}`}><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
          {items.length === 0 && <div className="empty">No agreements uploaded yet.</div>}
        </div>
      </div>

      <div className="card">
        <div className="card-head"><h2><PenLine size={15} style={{ display: "inline", marginRight: 6 }} />Signed agreements</h2><span className="badge success">{signed.length}</span></div>
        <div className="list-rows">
          {signed.map(s => (
            <div key={s.id} className="list-row" data-testid={`signed-row-${s.id}`}>
              <div className="list-row-info"><h3>{s.agreement_title}</h3><p>{s.artist_name} ({s.artist_public_id}) signed for {s.client_company || s.client_name} · {s.order_code} · {fmtDateTime(s.signed_at)} · IP {s.ip}</p></div>
              <button className="ghost" onClick={() => downloadFile(`/files/${s.id}`, {}, s.filename)} data-testid={`signed-download-${s.id}`}><Download size={13} /> Signed PDF</button>
            </div>
          ))}
          {signed.length === 0 && <div className="empty">No signed agreements yet.</div>}
        </div>
      </div>
    </>
  );
}
